/**
 * The site's own select.
 *
 * A native <select> hands the list to the operating system, so a form opened a
 * grey system menu in a system font with a system arrow, next to pages that
 * look nothing like it. This keeps the behaviour of a select and draws the list
 * in the site's own type, spacing and colour.
 *
 * Two shapes share this one component. A toolbar menu submits its form the
 * moment you pick (that is what a sort or a filter is for); a form field does
 * not, because picking a discount type must never save the whole form. The
 * `submits` flag is the only difference between them.
 *
 * Options are real buttons, so focus, Tab and screen readers work without
 * aria-activedescendant bookkeeping. The keyboard contract matches what people
 * already expect from a select: Down/Up move, Home/End jump, typing letters
 * jumps to a matching option, Enter or Space picks, Escape closes and puts
 * focus back on the trigger.
 */
export function registerSelectMenu(Alpine) {
    Alpine.data('selectMenu', (config = {}) => ({
        open: false,
        // Flipped above the trigger when the panel would otherwise run off the
        // bottom of the window, which is where the last field of a long form
        // always sits.
        up: false,
        value: config.value ?? '',
        options: config.options ?? [],
        submits: config.submits !== false,
        disabled: config.disabled ?? false,
        search: '',
        searchTimer: null,

        currentLabel() {
            const found = this.options.find((o) => String(o.value) === String(this.value));
            return found ? found.label : '';
        },

        isEmpty() {
            return this.value === '' || this.value === null || this.value === undefined;
        },

        toggle() {
            this.open ? this.close() : this.show();
        },

        show() {
            if (this.disabled) return;
            this.open = true;
            // Two hops: $nextTick for the x-for rows to exist, then a frame for
            // the panel to actually be displayed. Focusing a hidden element is
            // a no-op, which is what left the keyboard stranded on the trigger.
            this.$nextTick(() => requestAnimationFrame(() => {
                this.place();
                const items = this.items();
                const checked = items.find((el) => el.dataset.value === String(this.value));
                (checked ?? items[0])?.focus();
            }));
        },

        place() {
            const trigger = this.$refs.trigger?.getBoundingClientRect();
            const panel = this.$refs.list;
            if (!trigger || !panel) return;

            const needed = panel.offsetHeight + 12;
            this.up = (window.innerHeight - trigger.bottom) < needed && trigger.top > needed;
        },

        close(refocus = false) {
            if (!this.open) return;
            this.open = false;
            if (refocus) this.$refs.trigger?.focus();
        },

        items() {
            return Array.from(this.$refs.list?.querySelectorAll('[data-value]') ?? []);
        },

        move(step) {
            const items = this.items();
            if (items.length === 0) return;
            const at = items.indexOf(document.activeElement);
            const next = at === -1 ? 0 : (at + step + items.length) % items.length;
            items[next].focus();
        },

        edge(which) {
            const items = this.items();
            (which === 'first' ? items[0] : items[items.length - 1])?.focus();
        },

        /**
         * Type-to-jump, the one native-select habit people notice missing on a
         * long list. The buffer resets after a pause so "ro" finds Royal Canin
         * but a later "r" starts again.
         *
         * Not called type(): Alpine resolves an expression against the nearest
         * scope that has the name, and a form whose own state is `type` (the
         * promo form) would have this method shadow it. x-modelable then wrote
         * a string over the method and the menu broke on the next keypress.
         */
        typeahead(key) {
            if (typeof key !== 'string' || key.length !== 1 || key === ' ') return;

            clearTimeout(this.searchTimer);
            this.search += key.toLowerCase();
            this.searchTimer = setTimeout(() => { this.search = ''; }, 600);

            const match = this.items().find(
                (el) => (el.textContent || '').trim().toLowerCase().startsWith(this.search),
            );
            match?.focus();
        },

        pick(value) {
            this.value = value;
            this.open = false;

            // Write straight to the input rather than waiting for the binding to
            // flush, because the very next line may navigate away.
            const input = this.$refs.input;
            if (input) input.value = value;

            // One event for both shapes: a named field carries it on the hidden
            // input so $event.target.value works, and detail.value works either
            // way for a control that posts nothing and only drives Alpine state.
            (input ?? this.$el).dispatchEvent(
                new CustomEvent('change', { bubbles: true, detail: { value } }),
            );

            if (this.submits) {
                const form = this.$el.closest('form');
                if (form) form.requestSubmit();
                return;
            }

            this.$refs.trigger?.focus();
        },
    }));
}
